import { ArrowPathIcon, MagnifyingGlassIcon, TrashIcon } from "@heroicons/react/20/solid"
import MainStore from "./MainStore"
import useDataEvent from "./CustomHook"
import Panel from "./Panel"
import { PrimitiveCard } from "./PrimitiveCard"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { DebouncedInput } from "./@components/DebouncedInput"
import { DebouncedNumberInput } from "./@components/DebouncedNumberInput"
import { Accordion, AccordionItem, Switch } from "@heroui/react"

const mainstore = MainStore()

function QueryPaneInfo({primitive}){
    const asTitle = !primitive.referenceParameters.useTerms && !primitive.referenceParameters.hasOwnProperty("terms") && primitive.title

    const terms = (asTitle ? primitive.title : primitive.referenceParameters.terms) ?? ""
    const site = (primitive.referenceParameters.site) ?? ""

    const termList = terms.split(",").filter(d=>d).map(d=>d.trim())
    const siteList = site.split(",").filter(d=>d).map(d=>d.trim())

    const total = termList.length * siteList.length

    if( total < 120 ){
        return <></>
    }
    return <div className="w-full flex bg-yellow-100 text-sm font-semibold text-slate-600 place-items-center p-2 justify-center mt-2 rounded-md space-x-2">
        <FontAwesomeIcon icon="fa-solid fa-circle-info" /><p>This query will run as {total} searches</p>
    </div>
    

}

export function QueryPane({primitive, ...props}){
    useDataEvent("set_field set_parameter", primitive?.id)
    const asTitle = !primitive.referenceParameters.useTerms && !primitive.referenceParameters.hasOwnProperty("terms") && primitive.title

    const sourceOption = primitive.metadata.parameters.sources
        ? { ...primitive.metadata.parameters.sources, single: true }
        : undefined
    const sources = primitive.referenceParameters.sources ?? []
    let configParams
    let activeConfig = primitive.metadata.parameters.sources.options.find(d=>d.id === sources?.[0])?.config
    if(  activeConfig ){
        configParams = {
            sources: sourceOption,
            ...activeConfig
        }

    }
    
    return (<div className={props.terms === false ?? props.detail === false ? "" : "space-y-2"}>
                {props.terms !== false && <div className="w-full flex space-x-2 min-h-16">
                    <DebouncedInput
                        area
                        size="sm"
                        variant="bordered"
                        minRows={3}
                        value={asTitle ? primitive.title : primitive.referenceParameters.terms}
                        placeholder="Search terms"
                        className="grow text-sm text-slate-500"
                        onChange={(value)=>{
                            if( asTitle ){
                                primitive.title = value
                            }else{
                                primitive.setParameter("terms", value)
                            }
                        }}
                    />
                </div>}
                {props.detail !== false && (
                  props.detailsVariant === "panel"
                    ? (
                      <Panel
                        collapsable
                        noSpace
                        className="border rounded-md bg-gray-50 px-3 py-2 dark:bg-slate-900/60 dark:border-slate-700"
                        title="Details"
                      >
                        <div className="w-full flex-col text-xs mt-2 space-y-2">
                          <PrimitiveCard.Parameters
                            primitive={primitive}
                            activeParameters={configParams}
                            hidden="terms"
                            editing
                            leftAlign
                            mode="form"
                            density="sm"
                            showLabel
                            className="text-xs text-slate-500 gap-y-3"
                            fullList
                          />
                        <div
                          style={{ gridTemplateColumns: "minmax(min-content, 7rem) 1fr" }}
                          className="grid gap-2 text-sm"
                        >
                          <p className="pl-1 py-1 mr-2 shrink-0 grow-0 text-slate-500 dark:text-slate-300">Item limit</p>
                          <DebouncedNumberInput
                            variant="bordered"
                            minValue={1}
                            placeholder="Unlimited"
                            value={primitive.referenceParameters?.limit ?? ""}
                            onValueChange={(value)=>{
                              if( value === "" || value === null || typeof value === "undefined"){
                                if( primitive.referenceParameters?.limit !== undefined ){
                                  primitive.setField("referenceParameters.limit", null)
                                }
                                return
                              }
                              const parsed = Number(value)
                              if( Number.isNaN(parsed) ){
                                return
                              }
                              const next = Math.max(1, Math.floor(parsed))
                              if( primitive.referenceParameters?.limit !== next ){
                                primitive.setField("referenceParameters.limit", next)
                              }
                            }}
                          />
                          <p className="pl-1 py-1 mr-2 shrink-0 grow-0 text-slate-500 dark:text-slate-300">Pick random items</p>
                          <Switch
                            size="sm"
                            isSelected={primitive.referenceParameters?.limitRandom ?? false}
                            onValueChange={(value)=>{
                              primitive.setField("referenceParameters.limitRandom", value)
                            }}
                          />
                        </div>
                        </div>
                      </Panel>
                    )
                    : (
                      <Accordion
                        isCompact
                        selectionMode="single"
                        className={props.terms !== false ? "px-1" : ""}
                        itemClasses={{
                          base: props.detailsBorder === false
                            ? "border-0 rounded-none px-0 shadow-none"
                            : "border border-gray-200 rounded-md px-2",
                          trigger: props.detailsBorder === false ? "px-0" : undefined,
                          title: "text-sm font-medium text-slate-600",
                          content: props.detailsBorder === false ? "px-0 pb-2 pt-1" : "pb-2",
                        }}
                      >
                        <AccordionItem key="details" aria-label="Details" title="Details">
                          <div className="w-full flex-col text-xs my-2 space-y-2">
                            <PrimitiveCard.Parameters
                              primitive={primitive}
                              activeParameters={configParams}
                              hidden="terms"
                              editing
                              leftAlign
                              mode="form"
                              density="sm"
                              showLabel
                              className="text-xs text-slate-500 gap-y-3"
                              fullList
                            />
                            <DebouncedNumberInput
                              label="Item limit"
                              labelPlacement="outside"
                              variant="bordered"
                              minValue={1}
                              placeholder="Unlimited"
                              value={primitive.referenceParameters?.limit ?? ""}
                              onValueChange={(value)=>{
                                if( value === "" || value === null || typeof value === "undefined"){
                                  if( primitive.referenceParameters?.limit !== undefined ){
                                    primitive.setField("referenceParameters.limit", null)
                                  }
                                  return
                                }
                                const parsed = Number(value)
                                if( Number.isNaN(parsed) ){
                                  return
                                }
                                const next = Math.max(1, Math.floor(parsed))
                                if( primitive.referenceParameters?.limit !== next ){
                                  primitive.setField("referenceParameters.limit", next)
                                }
                              }}
                            />
                            <Switch
                              size="sm"
                              isSelected={primitive.referenceParameters?.limitRandom ?? false}
                              onValueChange={(value)=>{
                                primitive.setField("referenceParameters.limitRandom", value)
                              }}
                            >
                              Pick random items
                            </Switch>
                          </div>
                        </AccordionItem>
                      </Accordion>
                    )
                )}
            </div>)
}

QueryPane.Info = QueryPaneInfo
